import React, { useState, useEffect } from "react";
import {
  Modal,
  Space,
  Tag,
  Typography,
  Row,
  Col,
  Progress,
  Button,
  Alert,
  Statistic,
  Card,
} from "antd";
import moment from "moment-timezone";
import { motion } from "framer-motion";
import {
  ClockCircleOutlined,
  MailOutlined,
  UserOutlined,
  ThunderboltOutlined,
  InfoCircleOutlined,
  CloseOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";

const { Title, Text } = Typography;

// Helper function to format time remaining
const formatTimeRemaining = (seconds) => {
  if (!seconds && seconds !== 0) return "Calculating...";
  if (seconds === 0) return "Almost done...";

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = Math.floor(seconds % 60);

  const parts = [];
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (remainingSeconds > 0 || parts.length === 0)
    parts.push(`${remainingSeconds}s`);

  return parts.join(" ");
};

const PulseReportProgressDetailModal = ({ visible, pulseReport, progress, onClose }) => {
  const [refreshing, setRefreshing] = useState(false);
  const [showRefreshMessage, setShowRefreshMessage] = useState(false);

  if (!pulseReport) return null;

  const progressData = progress || {
    progress: {
      sent: 0,
      delivered: 0,
      failed: 0,
      total: 0,
      percentage: 0,
    },
    sendingRate: { current: 0, totalSenders: 0, effectiveRate: 0, configured: 0 },
    recentActivity: { lastFiveMinutes: 0 },
    timing: {},
    checkpoint: null,
  };

  // Calculate remaining emails
  const remainingEmails = progressData.progress.total - progressData.progress.sent;

  // Handler for refresh button
  const handleRefresh = () => {
    setRefreshing(true);
    setTimeout(() => {
      setRefreshing(false);
      setShowRefreshMessage(true);
      setTimeout(() => {
        setShowRefreshMessage(false);
      }, 3000);
    }, 1000);
  };

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            type="primary"
            icon={<ReloadOutlined spin={refreshing} />}
            loading={refreshing}
            onClick={handleRefresh}
          >
            Refresh Data
          </Button>
        </div>
      }
      width={800}
      title={
        <Space>
          <MailOutlined />
          <span>Pulse Report Progress - {pulseReport.name}</span>
        </Space>
      }
      closeIcon={<CloseOutlined />}
      destroyOnClose={true}
    >
      {/* Refresh success message */}
      {showRefreshMessage && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          style={{ marginBottom: "16px" }}
        >
          <Alert
            message="Data refreshed successfully"
            type="success"
            showIcon
            closable
            onClose={() => setShowRefreshMessage(false)}
          />
        </motion.div>
      )}

      {/* Summary Stats Row */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card size="small">
            <Statistic
              title="Completion"
              value={progressData.progress.percentage}
              suffix="%"
              valueStyle={{ color: "#1890ff" }}
              prefix={<ThunderboltOutlined />}
            />
            <Progress
              percent={Number(progressData.progress.percentage)}
              strokeColor={{
                "0%": "#1890FF",
                "100%": "#52C41A",
              }}
              size="small"
              showInfo={false}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card size="small">
            <Statistic
              title="Sending Rate"
              value={progressData.sendingRate.current || progressData.sendingRate.effectiveRate || 0}
              suffix="/hr"
              valueStyle={{ color: "#52c41a" }}
              prefix={<ThunderboltOutlined />}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              Configured: {progressData.sendingRate.configured || 50}/hr
            </Text>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card size="small">
            <Statistic
              title="Total Recipients"
              value={progressData.progress.total}
              valueStyle={{ color: "#722ed1" }}
              prefix={<MailOutlined />}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              Total emails to send
            </Text>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card size="small">
            <Statistic
              title="Active Senders"
              value={progressData.sendingRate.totalSenders || 1}
              valueStyle={{ color: "#722ed1" }}
              prefix={<UserOutlined />}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              {progressData.progress.sent} emails sent
            </Text>
          </Card>
        </Col>
      </Row>

      {/* Progress Section */}
      <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
        <Col xs={24} lg={12}>
          <Card
            title={
              <Space>
                <MailOutlined />
                <span>Email Distribution</span>
                <Tag color="blue">
                  {progressData.progress.sent}/{progressData.progress.total}
                </Tag>
              </Space>
            }
            size="small"
          >
            <Progress
              percent={Number(progressData.progress.percentage)}
              strokeColor="#1890FF"
              style={{ marginBottom: 16 }}
            />
            <Row gutter={[16, 16]}>
              <Col span={8}>
                <Statistic
                  title="Sent"
                  value={progressData.progress.sent}
                  valueStyle={{ color: "#52c41a", fontSize: 20 }}
                  prefix={<CheckCircleOutlined />}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Remaining"
                  value={remainingEmails}
                  valueStyle={{ color: "#1890ff", fontSize: 20 }}
                  prefix={<ClockCircleOutlined />}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Failed"
                  value={progressData.progress.failed}
                  valueStyle={{
                    color: progressData.progress.failed > 0 ? "#ff4d4f" : "#52c41a",
                    fontSize: 20,
                  }}
                  prefix={<ExclamationCircleOutlined />}
                />
              </Col>
            </Row>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card
            title={
              <Space>
                <ClockCircleOutlined />
                <span>Timing</span>
              </Space>
            }
            size="small"
          >
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Statistic
                  title="Time Remaining"
                  value={formatTimeRemaining(progressData.timing?.estimatedTimeRemaining)}
                  valueStyle={{ fontSize: 18 }}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Est. Completion"
                  value={
                    progressData.timing?.estimatedCompletionTime
                      ? moment(progressData.timing.estimatedCompletionTime).format("HH:mm, MMM DD")
                      : "Calculating..."
                  }
                  valueStyle={{ fontSize: 18 }}
                />
              </Col>
            </Row>
            {progressData.timing?.startedAt && (
              <div style={{ marginTop: 16 }}>
                <Space>
                  <InfoCircleOutlined style={{ color: "#1890ff" }} />
                  <Text type="secondary">
                    Started: {moment(progressData.timing.startedAt).format("HH:mm, MMM DD")}
                  </Text>
                </Space>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* Checkpoint Info */}
      {progressData.checkpoint && (
        <Card
          title={
            <Space>
              <InfoCircleOutlined />
              <span>Checkpoint Information</span>
            </Space>
          }
          size="small"
          style={{ marginBottom: 16 }}
        >
          <Row gutter={[16, 16]}>
            <Col span={8}>
              <Statistic
                title="Processed"
                value={progressData.checkpoint.processedCount || 0}
                valueStyle={{ fontSize: 18 }}
              />
            </Col>
            <Col span={8}>
              <Statistic
                title="Remaining"
                value={progressData.checkpoint.remainingCount || 0}
                valueStyle={{ fontSize: 18 }}
              />
            </Col>
            <Col span={8}>
              <Statistic
                title="Index"
                value={progressData.checkpoint.index || 0}
                valueStyle={{ fontSize: 18 }}
              />
            </Col>
          </Row>
          {progressData.checkpoint.runId && (
            <div style={{ marginTop: 12 }}>
              <Text type="secondary">Run ID: {progressData.checkpoint.runId}</Text>
            </div>
          )}
        </Card>
      )}

      {/* Recent Activity */}
      <Card
        title={
          <Space>
            <ThunderboltOutlined />
            <span>Recent Activity</span>
          </Space>
        }
        size="small"
      >
        <Row gutter={[16, 16]} align="middle">
          <Col span={8}>
            <Statistic
              title="Last 5 Minutes"
              value={progressData.recentActivity?.lastFiveMinutes || 0}
              suffix="emails"
              valueStyle={{ fontSize: 18, color: "#52c41a" }}
            />
          </Col>
          <Col span={16}>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Tag color="rgba(24, 144, 255, 0.1)" style={{ color: "#1890ff" }}>
                <ClockCircleOutlined /> Report will complete approximately{" "}
                {progressData.timing?.estimatedCompletionTime
                  ? moment(progressData.timing.estimatedCompletionTime).fromNow()
                  : "soon"}
              </Tag>
            </motion.div>
          </Col>
        </Row>
      </Card>
    </Modal>
  );
};

export default PulseReportProgressDetailModal;
